import React, { useCallback, useEffect, useState } from 'react';
import { PartialEditUserFormRequest, User } from '../../../../../api/interfaces/User';
import { Alert, Autocomplete, Box, Chip, CircularProgress, Typography } from '@mui/material';
import { PinnacleTextField } from '../../../../../components/PinnacleTextField';
import { School } from '../../../../../api/interfaces/School';
import { handleApiError } from '../../../../../utils/errorHandler';
import { pushNotification } from '../../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { NotificationTypes } from '../../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';
import { getTerms } from '../../../../../api/services/terms';
import { Term } from '../../../../../api/interfaces/Terms';
import { Program } from '../../../../../api/interfaces/Program';
import { updateUser } from '../../../../../api/services/user';
import moment from 'moment';

interface UserInformationTabProps {
    programs: Program[];
    schools: School[];
    formData: PartialEditUserFormRequest;
    onChange: (field: string, value: any) => void;
    onSuccess: (updatedUser: User, skipClose?: boolean) => void;
    selectedUserInfo?: User;
    disabled?: boolean;
    setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
}

const UserInformationTab: React.FC<UserInformationTabProps> = ({
    formData,
    onChange,
    onSuccess,
    selectedUserInfo,
    programs,
    schools,
    disabled,
    setIsSubmitting
}) => {
    const [terms, setTerms] = useState<Term[]>([]);
    const [loading, setLoading] = useState(true);
    const [isHandleTerms, setIsHandleTerms] = useState(false);
    const [isHandlePrograms, setIsHandlePrograms] = useState(false);

    const fetchTerms = useCallback(async () => {
        try {
            const termsResponse = await getTerms({ status: 'active' });
            setTerms(termsResponse.data);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, []);

    // Initial load
    useEffect(() => {
        const run = async () => {
            try {
                setIsSubmitting(true);
                setLoading(true);
                await fetchTerms();
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
            } finally {
                setIsSubmitting(false);
                setLoading(false);
            }
        };
        run();
    }, [fetchTerms]);

    const handleSelectedTerm = async (newValue: Term | null, reason: string) => {
        setIsHandleTerms(true);
        setIsHandlePrograms(true);
        try {
            if (selectedUserInfo) {
                const request: PartialEditUserFormRequest = {};
                if (reason === "clear") {
                    if (selectedUserInfo.programId) {
                        // Find the current program of the user
                        const currentProgram = programs.find((p) => p._id === selectedUserInfo.programId)

                        // Only set to trial if the assigned program is not undergrad
                        if (!currentProgram?.isUndergrad) {
                            request.isTrial = true;
                        }
                    } else {
                        // If no program, always set the user to isTrial
                        request.isTrial = true;
                    }
                    request.termId = null as any;
                } else if (newValue) {
                    request.isTrial = false;
                    request.termId = newValue._id;
                    request.programId = newValue.programId;
                }
                const updateUserResponse = await updateUser(selectedUserInfo._id, request);
                onSuccess(updateUserResponse.data, true);

                if (reason === 'clear') {
                    onChange("termId", "");
                } else {
                    onChange("termId", newValue ? newValue._id : "");
                    onChange("termName", updateUserResponse.data.termName);
                    onChange("programId", updateUserResponse.data.programId);
                    onChange("programName", updateUserResponse.data.programName);
                }
                onChange("isTrial", updateUserResponse.data.isTrial);
            }
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        } finally {
            setIsHandleTerms(false);
            setIsHandlePrograms(false);
        }
    }

    const handleSelectedProgram = async (newValue: Program | null) => {
        setIsHandlePrograms(true);
        try {
            if (selectedUserInfo && newValue) {
                const request = {
                    programId: newValue._id,
                } as any;
                const updateResponse = await updateUser(selectedUserInfo._id, request);
                onSuccess(updateResponse.data, true);
                onChange("programId", updateResponse.data.programId);
                onChange("programName", updateResponse.data.programName);
                onChange("isTrial", updateResponse.data.isTrial);
            }
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        } finally {
            setIsHandlePrograms(false);
        }
    }

    useEffect(() => {
        if (formData.termId && terms.length > 0) {
            const selectedTerm = terms.find((term) => term._id === formData.termId);
            if (selectedTerm?.programId) {
                if (!formData.programId || formData.programId !== selectedTerm.programId) {
                    onChange('programId', selectedTerm.programId);
                }
            }
        }
    }, [formData.termId, formData.programId, terms, onChange]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
            </Box>
        );
    }
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box sx={{ pb: 1 }}>
                <Alert severity="info" icon={false} sx={{ backgroundColor: '#E5F8FE', color: '#26C6F9' }}>
                    <Typography variant="subtitle2">
                        This user is currently
                        <Box component="span" sx={{ fontWeight: 'bold' }}>{selectedUserInfo?.isActive ? ' ACTIVE ' : ' INACTIVE '}</Box>
                        and {selectedUserInfo?.isTrial && 'on'} a
                        <Box component="span" sx={{ fontWeight: 'bold' }}>{selectedUserInfo?.isTrial ? ' TRIAL ' : ' SUBSCRIBER '}</Box>
                        {selectedUserInfo?.isTrial && 'status'}.
                    </Typography>
                </Alert>
            </Box>

            <Box
                display="flex"
                alignItems="left"
                justifyContent="space-between"
                sx={{ width: "100%" }}
            >
                <Typography variant="body1" sx={{ color: '#333533', fontWeight: '600' }}>
                    User Information
                </Typography>

                <Typography variant="body1" sx={{ color: '#A8A8A8', fontWeight: '600' }}>
                    1 of 3
                </Typography>
            </Box>

            <PinnacleTextField
                label="Email address"
                name="email"
                value={formData.email}
                fullWidth
                disabled
            />

            <PinnacleTextField
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={(e) => onChange("firstName", e.target.value)}
                fullWidth
                disabled={disabled}
            />

            <PinnacleTextField
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={(e) => onChange("lastName", e.target.value)}
                fullWidth
                disabled={disabled}
            />

            <Autocomplete
                freeSolo
                options={schools}
                getOptionLabel={(option) => {
                    // handle both object and string
                    if (typeof option === "string") return option;
                    return option.name;
                }}
                value={formData.school || ""}
                onChange={(_, newValue) => {
                    if (typeof newValue === "string") {
                        onChange("school", newValue);
                    } else {
                        onChange("school", newValue ? newValue.name : "");
                    }
                }}
                onInputChange={(_, newInputValue) => {
                    onChange("school", newInputValue);
                }}
                renderInput={(params) => (
                    <PinnacleTextField
                        {...params}
                        label="School"
                        fullWidth
                        disabled={disabled}
                    />
                )}
            />

            <Autocomplete
                options={terms}
                getOptionLabel={(option: Term) => option.name}
                value={terms.find((term) => term._id === formData.termId) || null}
                onChange={(_, newValue: Term | null, reason) => handleSelectedTerm(newValue, reason)}
                disabled={(disabled || isHandleTerms)}
                loading={isHandleTerms}
                renderInput={(params) => (
                    <PinnacleTextField
                        {...params}
                        label="Term"
                        fullWidth
                        slotProps={{
                            input: {
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {isHandleTerms ? <CircularProgress size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }
                        }}
                    />
                )}
            />

            <Autocomplete
                options={programs}
                getOptionLabel={(option: Program) => option.name}
                value={(formData.programId && programs.find((p) => p._id === formData.programId)) || undefined}
                onChange={(_, newValue: Program | null) => handleSelectedProgram(newValue)}
                disabled={!!formData.termId || disabled || isHandleTerms || isHandlePrograms}
                // disabled={((!!formData.termId || !selectedUserInfo?.isTrial) || disabled || isHandleTerms)}
                // disabled={
                //     disabled || 
                //     isHandleTerms || 
                //     isHandlePrograms ||
                //     (!selectedUserInfo?.isTrial && true) ||  // Non-trials: always disabled
                //     (selectedUserInfo?.isTrial && !!formData.termId) // Trial but has a term: disable
                // }
                loading={isHandleTerms || isHandlePrograms}
                disableClearable
                renderInput={(params) => (
                    <PinnacleTextField
                        {...params}
                        label="Program"
                        fullWidth
                        slotProps={{
                            input: {
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {(isHandleTerms || isHandlePrograms) ? <CircularProgress size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }
                        }}
                    />
                )}
            />
            {/* Display chip if the user is a trial user */}
            {!formData?.termId && (
                // Display if the user is a trial user
                (formData?.isTrial) ? (
                    <Chip label={`TRIAL | ${moment(formData.expiresAt).format('ll')}`} color="info" variant="outlined" sx={{ width: 160 }} />
                ) : programs.find((p) => p._id === formData.programId)?.isUndergrad && (
                    <Chip label={`UNDERGRAD | ${moment(formData.expiresAt).format('ll')}`} color="info" variant="outlined" sx={{ width: 200 }} />
                )
            )}
        </Box>
    );
};


export default UserInformationTab;
